import React, { createContext, useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/AuthService";
import UserService from "../services/UserService";
import AccessCodeService from "services/AccessCodeService";
import { AUTH_SESSION_EXPIRED_EVENT } from "../services/axiosConfig";
import LoadingComponent from "assets/theme/functions/loadingAnimation";

const defaultAuthContextValue = {
  user: null,
  token: null,
  login: async () => false,
  logout: async () => {},
  redirect: () => {},
  updateUser: async () => null,
  isAuthenticated: async () => false,
  hasAcceptedPrivacyConsent: () => false,
  acceptPrivacyConsent: async () => false,
};

const AuthContext = createContext(defaultAuthContextValue);

const PRIVACY_CONSENT_VERSION = "v1";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(AuthService.getCurrentUser());
  const [hasPrivacyConsent, setHasPrivacyConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const isUnloggedUser = (currentUser) =>
    String(currentUser?.Role?.Description || "").toLowerCase() === "unloggeduser";

  const isAllowedPathForUnloggedSession = (pathname) =>
    /^\/(ofertas\/[^/]+|pedidos\/[^/]+|access-quotation\/[^/]+|access-order\/[^/]+)\/?$/i.test(
      String(pathname || "")
    );

  const refreshPrivacyConsentStatus = async () => {
    try {
      const response = await UserService.getPrivacyConsentStatus();
      const consent = response?.data?.data;
      const accepted = Boolean(consent?.Accepted && consent?.AcceptedAt);
      setHasPrivacyConsent(accepted);
      return accepted;
    } catch {
      setHasPrivacyConsent(false);
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const authenticated = await AuthService.isAuthenticated();

      if (authenticated) {
        try {
          const currentUser = AuthService.getCurrentUser();

          if (isUnloggedUser(currentUser)) {
            const currentPathname = typeof window !== "undefined" ? window.location.pathname : "";

            if (!isAllowedPathForUnloggedSession(currentPathname)) {
              await AuthService.logout();
              AuthService.clearSessionData();
              setUser(null);
              setHasPrivacyConsent(false);
              setLoading(false);
              return;
            }

            setUser(currentUser);
            setHasPrivacyConsent(true);
            setLoading(false);
            return;
          }

          setUser(currentUser);
          await refreshPrivacyConsentStatus();
        } catch (error) {
          console.error("Erro ao buscar usuário atual:", error);
          AuthService.clearSessionData();
          setHasPrivacyConsent(false);
        }
      } else {
        setUser(null);
        setHasPrivacyConsent(false);
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      // Only redirect to login if the user had an active session.
      // If getCurrentUser() is null, the 401 came from the initial auth check
      // of an unauthenticated user — not a real session expiry.
      const hadActiveSession = !!AuthService.getCurrentUser();

      AuthService.clearSessionData();
      setUser(null);
      setHasPrivacyConsent(false);

      if (hadActiveSession && window.location.pathname !== "/login") {
        navigate("/login", {
          replace: true,
          state: {
            message: {
              type: "warning",
              msg: "Sua sessao expirou. Faca login novamente.",
            },
          },
        });
      }
    };

    window.addEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(AUTH_SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [navigate]);

  const login = async (email, password) => {
    try {
      await AuthService.login(email, password);
      
      const currentUser = AuthService.getCurrentUser();
      setUser(currentUser);

      // Derive consent from already-loaded user object (avoids an extra HTTP call during login)
      const acceptedConsent = Boolean(
        currentUser?.PrivacyConsentAcceptedAt && currentUser?.PrivacyConsentVersion
      );
      setHasPrivacyConsent(acceptedConsent);

      if (!acceptedConsent) {
        navigate("/consentimento-privacidade");
        return;
      }

      if (currentUser?.Role?.Description.toLowerCase() === "admin") {
        navigate("/opcoes");
      }
      else {
        navigate("/ofertas");
      }
      
    } catch (error) {
      console.error("Erro no login:", error);
      throw error;
    }
  };

  const logout = async () => {
    await AuthService.logout();
    AccessCodeService.clearGuestDisplayName();
    setUser(null);
    setHasPrivacyConsent(false);
    navigate("/login");
  };

  const updateUser = async () => {
    try {
      const updatedUser = await AuthService.fetchAndStoreUserData();
      setUser(updatedUser);
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
    }
  }

  const redirect = () => {
    if (!user) {
      navigate("/login");
    }
  };

  const isAuthenticated = () => AuthService.isAuthenticated();

  const hasAcceptedPrivacyConsent = (authUser = user) => {
    if (isUnloggedUser(authUser)) {
      return true;
    }

    return Boolean(authUser && hasPrivacyConsent);
  };

  const acceptPrivacyConsent = async () => {
    try {
      await UserService.acceptPrivacyConsent(PRIVACY_CONSENT_VERSION);
      setHasPrivacyConsent(true);
      return true;
    } catch (error) {
      console.error("Erro ao registrar consentimento de privacidade:", error);
      setHasPrivacyConsent(false);
      return false;
    }
  };


  if (loading) {
    return <LoadingComponent />;
  }


  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        token: null,
        login, 
        logout,
        redirect,
        updateUser, 
        isAuthenticated,
        hasAcceptedPrivacyConsent,
        acceptPrivacyConsent,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);